import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import Footer from '../components/Footer';

const CATEGORIES = ['All', 'School', 'Sports', 'Academic', 'Cultural', 'Events', 'General'];

const Gallery = () => {
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('All');
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedIdx, setSelectedIdx] = useState(0);

  useEffect(() => { fetchGallery(); }, []);

  const fetchGallery = async () => {
    try {
      const res = await fetch('./admin/api/gallery.php');
      const data = await res.json();
      if (data.success) setGallery(data.data);
    } catch { /* silent */ } finally {
      setLoading(false);
    }
  };

  const filtered = gallery.filter(item =>
    activeCategory === 'All' || item.category?.toLowerCase() === activeCategory.toLowerCase()
  );

  const openLightbox = useCallback((item, idx) => {
    setSelectedImage(item);
    setSelectedIdx(idx);
    document.body.style.overflow = 'hidden';
  }, []);

  const closeLightbox = useCallback(() => {
    setSelectedImage(null);
    document.body.style.overflow = '';
  }, []);

  const navigate = useCallback((dir) => {
    const newIdx = (selectedIdx + dir + filtered.length) % filtered.length;
    setSelectedIdx(newIdx);
    setSelectedImage(filtered[newIdx]);
  }, [selectedIdx, filtered]);

  useEffect(() => {
    const onKey = (e) => {
      if (!selectedImage) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') navigate(-1);
      if (e.key === 'ArrowRight') navigate(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedImage, navigate, closeLightbox]);

  const usedCategories = CATEGORIES.filter(cat =>
    cat === 'All' || gallery.some(item => item.category?.toLowerCase() === cat.toLowerCase())
  );

  return (
    <div className="page-enter">
      <Helmet>
        <title>Photo Gallery — PAK School &amp; College System | School Events &amp; Activities</title>
        <meta name="description" content="View our photo gallery showcasing school events, activities, achievements, and daily life at PAK School & College System Mardan." />
        <meta name="keywords" content="PAK School Gallery, School Photos, School Events, Student Life, School Activities Mardan" />
        <link rel="canonical" href="https://pakschoolandcollege.com/gallery" />
      </Helmet>

      {/* Hero */}
      <div className="page-hero">
        <div className="page-hero-content">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white/80 text-sm mb-5">
            📸 Capturing Memories
          </div>
          <h1>Photo <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">Gallery</span></h1>
          <p>A glimpse into the vibrant life, events, and achievements at PAK School &amp; College System</p>
        </div>
      </div>

      <div className="py-12 sm:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Category filter */}
          {usedCategories.length > 2 && (
            <div className="flex flex-wrap justify-center gap-2 mb-10">
              {usedCategories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-4 py-2 rounded-full font-poppins font-600 text-sm transition-all duration-200
                    ${activeCategory === cat
                      ? 'bg-primary text-white shadow-md shadow-primary/25'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          )}

          {/* Count */}
          {!loading && gallery.length > 0 && (
            <p className="text-center text-gray-400 font-inter text-sm mb-8">
              Showing {filtered.length} photo{filtered.length !== 1 ? 's' : ''}
              {activeCategory !== 'All' && ` in ${activeCategory}`}
            </p>
          )}

          {/* Loading */}
          {loading && (
            <div className="flex flex-col items-center py-24 gap-4">
              <div className="spinner" />
              <p className="text-gray-400 text-sm font-inter">Loading gallery...</p>
            </div>
          )}

          {/* Empty */}
          {!loading && gallery.length === 0 && (
            <div className="text-center py-24">
              <div className="w-24 h-24 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-6">
                <i className="fas fa-camera text-4xl text-gray-300" />
              </div>
              <h3 className="font-poppins font-700 text-gray-600 text-xl mb-2">No Photos Yet</h3>
              <p className="text-gray-400 font-inter">We're capturing amazing moments to share soon!</p>
            </div>
          )}

          {/* Gallery grid */}
          {!loading && filtered.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
              {filtered.map((item, idx) => (
                <div
                  key={item.id}
                  className="group relative overflow-hidden bg-gray-100 rounded-xl sm:rounded-2xl cursor-pointer hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5"
                  style={{ aspectRatio: '1/1' }}
                  onClick={() => openLightbox(item, idx)}
                  role="button"
                  tabIndex={0}
                  aria-label={`View ${item.title || 'photo'}`}
                  onKeyDown={(e) => e.key === 'Enter' && openLightbox(item, idx)}
                >
                  <img
                    src={`admin/${item.image_path}`}
                    alt={item.title || 'School photo'}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-navy/0 group-hover:bg-navy/40 transition-all duration-300 flex items-center justify-center">
                    <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center scale-0 group-hover:scale-100 transition-transform duration-300 shadow-lg">
                      <i className="fas fa-expand text-navy" />
                    </div>
                  </div>
                  {item.title && (
                    <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-navy/80 to-transparent p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <p className="text-white text-xs font-inter truncate">{item.title}</p>
                      {item.category && (
                        <span className="inline-block bg-primary/80 text-white text-[10px] px-2 py-0.5 rounded-full mt-1 font-poppins">{item.category}</span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Lightbox */}
      {selectedImage && (
        <div
          className="lightbox-overlay"
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
        >
          <button
            className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
            onClick={closeLightbox}
            aria-label="Close"
          >
            <i className="fas fa-times" />
          </button>
          <div className="absolute top-4 left-4 text-white/60 text-sm font-inter">
            {selectedIdx + 1} / {filtered.length}
          </div>
          {filtered.length > 1 && (
            <>
              <button
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
                onClick={(e) => { e.stopPropagation(); navigate(-1); }}
                aria-label="Previous"
              >
                <i className="fas fa-chevron-left" />
              </button>
              <button
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
                onClick={(e) => { e.stopPropagation(); navigate(1); }}
                aria-label="Next"
              >
                <i className="fas fa-chevron-right" />
              </button>
            </>
          )}
          <div
            className="max-w-4xl max-h-[80vh] w-full mx-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={`admin/${selectedImage.image_path}`}
              alt={selectedImage.title || 'Photo'}
              className="w-full max-h-[75vh] object-contain rounded-2xl shadow-2xl"
            />
            {selectedImage.title && (
              <p className="text-center text-white/70 text-sm font-inter mt-3">{selectedImage.title}</p>
            )}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Gallery;
